<template>
  <Wrapper>
    <Topo v-if="isMobile" />
    <LoginMain>
      <v-card
        :class="isMobile ? 'mx-auto' : ''"
        :max-width="isMobile ? '400px' : '585px'"
        color="transparent"
        outlined="outlined"
      >
        <LoginTitulo>
          <template v-slot:titulo>
            {{ tituloDaPagina }}
          </template>
          <template v-slot:subtitulo>
            Crie uma nova senha para ter acesso à plataforma.
          </template>
        </LoginTitulo>

        <v-form @submit.prevent="changePassword()">
          <v-col class="my-0 pa-0">
            <v-text-field
              v-model="password"
              background-color="#FFF"
              label="Nova senha"
              filled
              data-cy="input_newPassword"
              :append-icon="show1 ? 'visibility' : 'visibility_off'"
              :type="show1 ? 'text' : 'password'"
              :disabled="disabled"
              @click:append="show1 = !show1"
              @focus="shouldShowPasswordRules = true"
            />

            <transition name="slide-fade">
              <div v-show="showPasswordRules" class="caption mt-n4">
                <p class="grey--text text--darken-2 caption mb-0">
                  Por motivos de segurança, sua senha precisa obedecer os seguintes critérios:
                </p>
                <ul id="rules" class="rules mb-5">
                  <li
                    v-for="(rule, ruleKey) in rules"
                    :key="ruleKey"
                    :class="rulesStyle[ruleKey].color + '--text'"
                  >
                    <v-icon
                      :color="rulesStyle[ruleKey].color"
                      x-small
                      class="mr-2"
                      v-text="'fas fa-' + rulesStyle[ruleKey].icon"
                    />
                    <span v-html="rule" />
                  </li>
                </ul>
              </div>
            </transition>

            <v-text-field
              v-model="passwordConfirmation"
              label="Confirmar a nova senha"
              filled
              data-cy="input_confirmPassword"
              background-color="#FFF"
              :append-icon="show2 ? 'visibility' : 'visibility_off'"
              :type="show2 ? 'text' : 'password'"
              :disabled="disabled"
              :error-messages="errorMessagesPasswordConfirmation"
              @click:append="show2 = !show2"
            />

            <v-btn
              :block="isMobile"
              :disabled="disabled"
              :loading="loading"
              :width="isDesktop ? '50%' : ''"
              color="primary"
              dark
              data-cy="button_redefinir"
              type="submit"
            >
              Alterar minha senha
            </v-btn>
          </v-col>
        </v-form>
      </v-card>
      <SnackBar
        :mensage="snackBarMessage"
        :color="snackBarColor"
        :snackbar="showSnackBar"
        :button="buttonSnackBar"
        :timeout="timeoutSnackBar"
        @changeSnackBar="changeSnackBar"
      />
    </LoginMain>
    <Rodape
      :exibe-central="true"
    />
  </Wrapper>
</template>

<script>

import { sameAs } from 'vuelidate/lib/validators';
import { getParamUrl, passwordStrengthRules } from '@/utils';
import ApiAuth from '@/api/auth';
import Wrapper from '../components/Wrapper.vue';
import LoginMain from '../components/Login/LoginMain.vue';
import LoginTitulo from '../components/Login/LoginTitulo.vue';
import SnackBar from '../components/SnackBar.vue';
import Topo from '../components/Topo.vue';
import Rodape from '../components/Rodape.vue';

export default {
  name: 'RedefinirSenha',

  components: {
    Wrapper,
    LoginTitulo,
    LoginMain,
    SnackBar,
    Topo,
    Rodape,
  },

  data() {
    return {
      show1: false,
      show2: false,
      password: '',
      passwordConfirmation: '',
      email: getParamUrl('email'),
      user: getParamUrl('u'),
      token: getParamUrl('t'),
      date: getParamUrl('d'),
      loading: false,
      snackBarMessage: '',
      snackBarColor: 'error',
      showSnackBar: false,
      shouldShowPasswordRules: false,
      rulesResult: {},
      disabled: false,
      success: false,
      buttonSnackBar: false,
      timeoutSnackBar: 5000,
      rules: {
        minLength: 'Deve conter no mínimo <b>8 caracteres</b>.',
        ucCharacter: 'Deve conter pelo menos <b>uma letra maiúscula</b>.',
        lcCharacter: 'Deve conter pelo menos <b>uma letra minúscula</b>.',
        number: 'Deve conter pelo menos <b>um número</b>.',
        spCharacter: 'Deve conter pelo menos <b>um símbolo</b> (ex: #!.?@)',
      },
      tituloDaPagina: 'Alterar senha',
      expiredLinkMessage: 'Link expirado ou inválido. Recupere a sua senha novamente.',
      passwordConfirmationErrorMessage: ['A nova senha é diferente da confirmação.'],
      genericErrorMessage: 'Ocorreu um erro.',
      connectionErrorMessage: 'Ocorreu um erro. Por favor, tente novamente mais tarde.',
      successMessage: 'Senha redefinida com sucesso.',
    };
  },

  validations() {
    return {
      passwordConfirmation: {
        sameAsPassword: sameAs('password'),
      },
    };
  },

  computed: {
    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    showPasswordRules() {
      return this.shouldShowPasswordRules && !this.rulesResult.valid;
    },
    errorMessagesPasswordConfirmation() {
      if (this.$v.passwordConfirmation.$dirty && this.$v.passwordConfirmation.$invalid) {
        return this.passwordConfirmationErrorMessage;
      }

      return [];
    },
    rulesStyle() {
      return Object.keys(this.rules).reduce((rulesStyleAcumulator, ruleKey) => {
        const rulesStyle = rulesStyleAcumulator;
        rulesStyle[ruleKey] = {
          color: this.rulesResult[ruleKey] ? 'success' : 'error',
          icon: this.rulesResult[ruleKey] ? 'check' : 'times',
        };
        return rulesStyle;
      }, {});
    },
  },

  watch: {
    password(password) {
      this.rulesResult = passwordStrengthRules(password);
    },
  },

  methods: {
    async changePassword() {
      this.shouldShowPasswordRules = true;
      this.$v.$touch();
      if (this.$v.$invalid || !this.rulesResult.valid) {
        return;
      }

      if (!this.token || !this.user || !this.date) {
        this.showMessage(this.expiredLinkMessage);
        return;
      }

      const formData = new FormData();
      formData.set('password', this.password);
      formData.set('confirmPassword', this.passwordConfirmation);
      formData.set('token', this.token);
      formData.set('date', this.date);
      formData.set('idUser', this.user);
      this.loading = true;

      try {
        const data = await ApiAuth.resetPassword(formData);

        if (!data) {
          throw new Error(this.genericErrorMessage);
        }

        this.disabled = true;
        this.success = true;
        this.showMessage(this.successMessage, 'success', true, 0);
      } catch (error) {
        this.showMessage(this.connectionErrorMessage);
      }

      this.loading = false;
      this.password = '';
      this.passwordConfirmation = '';
      this.shouldShowPasswordRules = false;
    },

    showMessage(message, color = 'error', button = true, time = 5000) {
      this.snackBarMessage = message;
      this.snackBarColor = color;
      this.buttonSnackBar = button;
      this.timeoutSnackBar = time;
      this.showSnackBar = true;
    },

    changeSnackBar(snackBar) {
      this.showSnackBar = snackBar.visible;
      if (!snackBar.visible && snackBar.$options.propsData.color === 'success') {
        this.$router.push('/');
      }
    },
  },

};
</script>


<style scoped>

.rules {
  list-style: none;
  padding: 0;
}

</style>
